import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import api, { apiRoutes } from '../../../api';
import { getConfig } from '../../../config/environment';
import { trackAddToCart, trackBeginCheckout } from '../../../helpers/tlfAnalytics';
import {
  appendSelectedPartnerToPath,
  applyPartnerToTrackingFields,
  capturePartnerSelectionFromLocation,
  getSelectedPartner,
  hasVerifiedPartnerSelection,
  invalidateSelectedPartner,
  normalizePartnerSelection,
  resolvePartnerFromList,
  storeSelectedPartner,
} from '../../../utils/partnerSelection';
import {
  resolveCtaEncryptedEnvelopeAutoFinalize,
  resolveCtaEncryptedEnvelopeMode,
  resolveCtaPostDrawEnvelopeMode,
} from '../../../utils/ctaHelpers';
import { useTranslations } from '../../../contexts/TranslationContext';
import usePartnerData from '../../../hooks/usePartnerData';
import { partnerIsRequiredOn } from '../../../utils/partnerData';
import { isDrawClosed as checkDrawClosed } from '../../../utils/drawLifecycle';

const PurchasePageContext = createContext(null);

const CART_STORAGE_KEY = 'tlffs_purchase_cart';

export function PurchasePageProvider({ children, gameTypeProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { gameType: ctxGameType } = useGameType();
  const gameType = gameTypeProp || ctxGameType;
  const { isLoggedIn } = useAuth();
  const { isGuestCheckoutEnabled, isAnonymousGuestCheckoutEnabled } = useAuthConfig();
  const { siteDetails, plans, nextDraw, loading: siteLoading, isDrawClosed } = useSiteConfig();
  const { t: tr } = useTranslations();
  const apiPlans = plans;
  const mainDraw = nextDraw;
  const { tlfId } = getConfig();
  const initialPartner = useMemo(() => {
    const partnerFromLocation = capturePartnerSelectionFromLocation(location);
    if (partnerFromLocation) return partnerFromLocation;
    return getSelectedPartner();
  }, [searchParams, location]);

  const [selectedPartner, setSelectedPartnerState] = useState(initialPartner);

  // Keep context partner in sync with URL/search partner changes
  useEffect(() => {
    setSelectedPartnerState(initialPartner);
  }, [initialPartner]);

  const setSelectedPartner = useCallback((partner) => {
    const normalized = storeSelectedPartner(partner);
    setSelectedPartnerState(normalized);
    if (normalized?.id) {
      setLimitationError((prev) => (
        prev === (tr('purchase.errors.partnerRequired') || 'Please choose a partner to support before checking out.')
          ? null
          : prev
      ));
    }
  }, [tr]);

  // ─── PURCHASE FLOW STATE ────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [packageQuantities, setPackageQuantities] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.gameType === gameType && parsed.packageQuantities) {
          return parsed.packageQuantities;
        }
      }
    } catch {}
    return {};
  });
  const [numWeeks, setNumWeeks] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.gameType === gameType && parsed.numWeeks) {
          return parsed.numWeeks;
        }
      }
    } catch {}
    return 1;
  });

  // ─── CTA CARD SELECTION ────────────────────────────────────────────────────
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardNum, setSelectedCardNum] = useState(null);
  const [availableCards, setAvailableCards] = useState([]);
  const [ctaSections, setCtaSections] = useState([]);

  const isManualEnvelopeMode = useMemo(
    () => gameType === 'chaseTheAce' && resolveCtaPostDrawEnvelopeMode(siteDetails, ctaSections),
    [gameType, siteDetails, ctaSections]
  );
  const isEncryptedEnvelopeMode = useMemo(
    () => gameType === 'chaseTheAce' && resolveCtaEncryptedEnvelopeMode(siteDetails, ctaSections),
    [gameType, siteDetails, ctaSections]
  );
  const isEncryptedEnvelopeAutoFinalize = useMemo(
    () => gameType === 'chaseTheAce' && resolveCtaEncryptedEnvelopeAutoFinalize(siteDetails, ctaSections),
    [gameType, siteDetails, ctaSections]
  );

  // ─── PERSIST CART STATE ACROSS PAGES (CTA card page) ────────────────────────
  useEffect(() => {
    const itemCount = Object.values(packageQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
    if (itemCount > 0) {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        packageQuantities,
        numWeeks,
        gameType,
      }));
    }
  }, [packageQuantities, numWeeks, gameType]);

  // ─── VALIDATION & LIMITS ────────────────────────────────────────────────────
  const [minimumPurchaseAmount, setMinimumPurchaseAmount] = useState(0);
  const [limitationError, setLimitationError] = useState(null);

  // ─── TERMS & CONDITIONS ─────────────────────────────────────────────────────
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ─── TICKET TRACKING ────────────────────────────────────────────────────────
  const [ticketTrackingFields, setTicketTrackingFields] = useState([]);
  const [trackingLoaded, setTrackingLoaded] = useState(false);
  const [trackingValidationFailed, setTrackingValidationFailed] = useState(false);

  // ─── PAYMENT STATE ──────────────────────────────────────────────────────────
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const selectingRef = useRef(false);

  // Reset payment processing when arriving on or returning to the purchase page
  useEffect(() => {
    setPaymentProcessing(false);
    selectingRef.current = false;
    setIsSelecting(false);
  }, [location.pathname]);

  // ─── CONFIRMATION ───────────────────────────────────────────────────────────
  const [orderRef, setOrderRef] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [generatingTickets, setGeneratingTickets] = useState(false);
  const [ticketsGenerated, setTicketsGenerated] = useState(0);
  const [generatedTicketNumbers, setGeneratedTicketNumbers] = useState([]);

  // ─── GUEST INFO (for guest checkout) ───────────────────────────────────────
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '', phone: '' });
  const [guestErrors, setGuestErrors] = useState({});

  // ─── FETCH MINIMUM PURCHASE ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchMinPurchase = async () => {
      try {
        const res = await api.get(apiRoutes.MinimumPurchase);
        const payload = res.data?.payload;
        if (payload?.minPurchaseEnabled) {
          setMinimumPurchaseAmount(payload.minPurchaseAmount || 0);
        }
      } catch (err) {
        console.debug('Could not fetch minimum purchase:', err);
      }
    };
    // Requires an authenticated token. Guests have no token until loginAsGuest
    // runs at checkout, so skip here to avoid 401s — the backend still enforces
    // the minimum server-side when the cart is created.
    if (isLoggedIn) fetchMinPurchase();
  }, [isLoggedIn]);

  // ─── PURCHASE PAGE PARTNER DATA ───────────────────────────────────────────
  const {
    partners: purchasePagePartners,
    fieldLabel: purchasePageFieldLabel,
    displayConfig: purchasePageDisplayConfig,
    loading: purchasePagePartnersLoading,
  } = usePartnerData(null, !!tlfId, tlfId, false, null, 'PurchasePage');

  const partnerRequiredOnPurchase = useMemo(() => {
    if (purchasePageDisplayConfig?.requiredOn) {
      const required = purchasePageDisplayConfig.requiredOn.some(
        (s) => String(s).toLowerCase() === 'purchasepage'
      );
      if (required) return true;
    }
    return (purchasePagePartners || []).some((partner) =>
      partnerIsRequiredOn(partner, 'PurchasePage')
    );
  }, [purchasePageDisplayConfig, purchasePagePartners]);

  useEffect(() => {
    if (purchasePagePartnersLoading || !selectedPartner) return;
    if (!purchasePagePartners || purchasePagePartners.length === 0) return;
    const resolved = resolvePartnerFromList(purchasePagePartners, selectedPartner);
    if (!resolved) {
      invalidateSelectedPartner(selectedPartner);
      setSelectedPartnerState(null);
    } else if (
      String(resolved.id) !== String(selectedPartner.id || '') ||
      !selectedPartner.name
    ) {
      const normalized = storeSelectedPartner(resolved);
      setSelectedPartnerState(normalized);
    }
  }, [purchasePagePartners, purchasePagePartnersLoading, selectedPartner]);

  const isPartnerValidOnPurchase = useMemo(() => {
    if (!partnerRequiredOnPurchase) return true;
    if (purchasePagePartnersLoading) return false;
    return hasVerifiedPartnerSelection(selectedPartner, purchasePagePartners);
  }, [partnerRequiredOnPurchase, purchasePagePartnersLoading, selectedPartner, purchasePagePartners]);

  // ─── FETCH TICKET TRACKING FIELDS ──────────────────────────────────────────
  useEffect(() => {
    const fetchTrackingFields = async () => {
      try {
        const res = await api.get(`${apiRoutes.TicketTrackingFields}?OrgId=${tlfId}`);
        const fields = res.data?.payload?.ticketTrackingFields || [];
        setTicketTrackingFields(applyPartnerToTrackingFields(fields, selectedPartner));
      } catch (err) {
        console.warn('Could not fetch tracking fields:', err);
      } finally {
        setTrackingLoaded(true);
      }
    };
    // Tracking fields require an authenticated token. Guests collect these on
    // the checkout page after loginAsGuest, so skip here to avoid 401s.
    if (tlfId && isLoggedIn) {
      fetchTrackingFields();
    } else {
      setTrackingLoaded(true);
    }
  }, [tlfId, isLoggedIn, selectedPartner]);

  const partnerFieldLabel = useMemo(() => {
    if (purchasePageFieldLabel) return purchasePageFieldLabel;
    const partnerField = (ticketTrackingFields || []).find(
      (f) => f.isPartner || (f.ticketTrackingFieldOptions || f.options || []).some(o => (o.ticketTrackingPartnerId != null || o.TicketTrackingPartnerId != null))
    );
    return partnerField?.fieldName || partnerField?.FieldName || partnerField?.name || partnerField?.Name || '';
  }, [purchasePageFieldLabel, ticketTrackingFields]);

  // ─── FETCH CTA SECTION SETTINGS ──────────────────────────────────────────
  useEffect(() => {
    if (gameType !== 'chaseTheAce' || !tlfId) return;
    const fetchCtaSections = async () => {
      try {
        const res = await api.get(apiRoutes.PublicPageSections, {
          params: { organizationId: tlfId, pageName: 'chasetheace_home' },
        });
        const sections = res.data?.payload?.sections || [];
        setCtaSections(sections.map(section => ({
          id: section.id,
          section_type: section.section_type || section.sectionType,
          config: section.config || {},
        })));
      } catch (err) {
        console.debug('Could not fetch CTA manual envelope setting:', err);
        setCtaSections([]);
      }
    };
    fetchCtaSections();
  }, [gameType, tlfId]);

  // ─── FETCH CTA CARD DECK ─────────────────────────────────────────────────
  useEffect(() => {
    if (gameType !== 'chaseTheAce' || !tlfId) return;
    if (isManualEnvelopeMode) {
      setAvailableCards([]);
      return;
    }
    const fetchDeck = async () => {
      try {
        const res = await api.get(apiRoutes.CtaPickACard, { params: { organizationId: tlfId } });
        const cards = res.data?.payload?.data || res.data?.payload || res.data || [];
        if (Array.isArray(cards) && cards.length > 0) {
          setAvailableCards(cards);
        } else {
          // Fallback: 52 numbered cards
          setAvailableCards(Array.from({ length: 52 }, (_, i) => ({
            id: i + 1,
            cardNumber: i + 1,
            isAvailable: true,
          })));
        }
      } catch (err) {
        console.warn('Could not fetch CTA card deck:', err);
        setAvailableCards(Array.from({ length: 52 }, (_, i) => ({
          id: i + 1,
          cardNumber: i + 1,
          isAvailable: true,
        })));
      }
    };
    fetchDeck();
  }, [gameType, tlfId, isManualEnvelopeMode]);

  // ─── NORMALIZED PACKAGES FROM API ──────────────────────────────────────────
  // Mirrors GamePurchasePage.apiPackages — map server plans to the shape the
  // ticket selector renders. Returns null when plans haven't loaded yet so the
  // UI can show a proper loading/empty state instead of placeholder prices.
  // "Best Value!" is computed locally — lowest price-per-ticket wins, with the
  // larger pack breaking ties. Single-plan organizations show no badge.
  const packages = useMemo(() => {
    if (!apiPlans || apiPlans.length === 0) return null;
    const active = apiPlans
      .filter(plan => plan.isActive !== false)
      .sort((a, b) => (a.sortIndex ?? 99) - (b.sortIndex ?? 99));

    let bestPlanId = null;
    if (active.length > 1) {
      const ratios = active.map(plan => {
        const tix = plan.numberOfTickets ?? plan.quantity ?? 1;
        const cost = plan.cost ?? plan.price ?? 0;
        return {
          id: plan.id,
          tickets: tix,
          ratio: tix > 0 ? cost / tix : Number.POSITIVE_INFINITY,
        };
      });
      const minRatio = Math.min(...ratios.map(r => r.ratio));
      // Tie-break: largest pack wins among lowest price-per-ticket.
      const winners = ratios.filter(r => Math.abs(r.ratio - minRatio) < 1e-6);
      winners.sort((a, b) => b.tickets - a.tickets);
      bestPlanId = winners[0]?.id ?? null;
    }

    return active.map((plan, idx) => {
      const isBest = bestPlanId != null && plan.id === bestPlanId;
      return {
        id: plan.id || idx + 1,
        tickets: plan.numberOfTickets ?? plan.quantity ?? 1,
        price: plan.cost ?? plan.price ?? 0,
        label: plan.name || `${plan.numberOfTickets ?? 1} for $${plan.cost ?? 0}`,
        savings: isBest ? 'Best Value!' : null,
        savingsKey: isBest ? 'purchase.ticket_selector.bestValue' : null,
        apiPlanId: plan.id,
      };
    });
  }, [apiPlans]);

  // ─── CART TOTAL CALCULATION ────────────────────────────────────────────────
  const cartTotal = useCallback(() => {
    if (!packages || packages.length === 0) return 0;
    let total = 0;
    packages.forEach(pkg => {
      const qty = packageQuantities[pkg.id] || 0;
      total += qty * (pkg.price || 0);
    });
    // Modified 50/50 multiplies by number of weeks
    if (gameType === 'modified5050' && numWeeks > 1) {
      total *= numWeeks;
    }
    return total;
  }, [packageQuantities, packages, gameType, numWeeks]);

  const cartItemCount = useCallback(() => {
    return Object.values(packageQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
  }, [packageQuantities]);

  // ─── CART ACTIONS ───────────────────────────────────────────────────────────
  const incrementPackage = useCallback((packageId) => {
    setPackageQuantities(prev => ({
      ...prev,
      [packageId]: (prev[packageId] || 0) + 1,
    }));
  }, []);

  const decrementPackage = useCallback((packageId) => {
    setPackageQuantities(prev => {
      const newQty = Math.max(0, (prev[packageId] || 0) - 1);
      if (newQty === 0) {
        const { [packageId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [packageId]: newQty };
    });
  }, []);

  const clearCart = useCallback(() => {
    setPackageQuantities({});
    setSelectedCard(null);
    setSelectedCardNum(null);
    sessionStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  // ─── CHECKOUT NAVIGATION ───────────────────────────────────────────────────
  // Mirrors GamePurchasePage.handlePaymentSubmit for standard lottery games:
  // validate → check limits → add to cart → initialize checkout → navigate to /checkout.
  const proceedToCheckout = useCallback(async () => {
    if (paymentProcessing) return;
    setLimitationError(null);
    setPaymentError(null);

    // If draw is closed, redirect to /closed and block checkout
    const drawClosed = (!siteLoading && isDrawClosed) || checkDrawClosed({ nextDraw, siteDetails });
    if (drawClosed) {
      navigate('/closed', { replace: true });
      return;
    }

    // Validation
    if (!packages || packages.length === 0) {
      setPaymentError(tr('purchase.errors.plansLoading') || 'Purchase plans are still loading. Please wait a moment and try again.');
      return;
    }
    if (cartItemCount() === 0) {
      setLimitationError(tr('purchase.errors.selectPackage') || 'Please select at least one ticket package.');
      return;
    }
    const totalPrice = cartTotal();
    const totalTickets = Object.entries(packageQuantities).reduce((sum, [pkgId, qty]) => {
      const pkg = packages.find(p => String(p.id) === String(pkgId));
      return sum + (qty || 0) * (pkg?.tickets || 0);
    }, 0);
    if (minimumPurchaseAmount > 0 && totalPrice < minimumPurchaseAmount) {
      setLimitationError(`${tr('purchase.errors.minimumPurchasePrefix') || 'Minimum purchase is'} $${minimumPurchaseAmount}.`);
      return;
    }
    if (gameType === 'chaseTheAce' && !isManualEnvelopeMode && !selectedCard) {
      setLimitationError(tr('purchase.errors.pickCard') || 'Please pick an envelope before checking out.');
      return;
    }
    if (partnerRequiredOnPurchase && !isPartnerValidOnPurchase) {
      setLimitationError(tr('purchase.errors.partnerRequired') || 'Please choose a partner to support before checking out.');
      try {
        window.dispatchEvent(new CustomEvent('partner-required-focus'));
      } catch {}
      return;
    }

    setPaymentProcessing(true);
    try {
      // Build purchases payload (needed for the logged-in cart POST here and for
      // handing the intent to the checkout page for guests).
      const purchases = [];
      packages.forEach(pkg => {
        const qty = packageQuantities[pkg.id] || 0;
        if (qty > 0) {
          purchases.push({
            PlanID: String(pkg.apiPlanId || pkg.id),
            NumberOfTimesSelected: qty,
            MemberTicketId: null,
          });
        }
      });
      const hasRealPlanIds = purchases.every(p => p.PlanID.includes('-'));
      if (!hasRealPlanIds) {
        throw new Error('Purchase plans are still loading. Please wait a moment and try again.');
      }

      const numOfDraws = gameType === 'modified5050' ? numWeeks : 1;
      const organizationCardId = (gameType === 'chaseTheAce' && !isManualEnvelopeMode)
        ? selectedCard
        : null;

      // ── GUEST CHECKOUT: anonymous session cart built server-side ──
      // Shopper goes directly to checkout where billing information is collected
      // as part of the payment flow (matching Stripe).
      if (!isLoggedIn && isGuestCheckoutEnabled()) {
        sessionStorage.removeItem(CART_STORAGE_KEY);
        trackBeginCheckout({
          cartTotal: totalPrice,
          ticketCount: totalTickets,
          lotteryType: gameType,
        });

        const sessionId = (crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        try {
          const addRes = await api.post(apiRoutes.GuestAnonymousCartAdd, {
            SessionId: sessionId,
            OrganizationId: tlfId,
            NumberOfDraws: numOfDraws,
            OrganizationCardId: organizationCardId,
            CartItems: purchases.map(p => ({
              PlanID: p.PlanID,
              NumberOfTimesSelected: p.NumberOfTimesSelected,
              MemberTicketId: p.MemberTicketId,
            })),
          });
          if (addRes.data?.payload?.status === 'Success') {
            navigate('/checkout', {
              state: {
                guestAnon: true,
                sessionId,
                gameType,
                totalPrice,
                totalTickets,
                organizationCardId: organizationCardId || selectedCard,
                envelopeNumber: selectedCardNum,
                purchases,
                numOfDraws,
              },
            });
            return;
          }
        } catch (anonErr) {
          console.warn('Anonymous cart add error:', anonErr);
        }

        navigate('/checkout', {
          state: {
            guestAnon: true,
            sessionId,
            purchases,
            numOfDraws,
            organizationCardId: organizationCardId || selectedCard,
            envelopeNumber: selectedCardNum,
            gameType,
            totalPrice,
            totalTickets,
          },
        });
        return;
      }
      if (!isLoggedIn) {
        setPaymentProcessing(false);
        return;
      }

      // Check purchase limitations (non-blocking on failure)
      try {
        const limRes = await api.get(
          `${apiRoutes.CheckPurchaseLimitations}?OrgId=${tlfId}&PurchaseDollarAmount=${totalPrice}&PurchaseTicketsAmount=${totalTickets}&NumberOfDraws=${numOfDraws}`
        );
        const limPayload = limRes.data?.payload;
        if (limPayload && limPayload.canProceed === false) {
          const msg = [limPayload.errorMessage1, limPayload.errorMessage2].filter(Boolean).join(' ')
            || tr('purchase.errors.purchaseLimitExceeded') || 'Purchase limit exceeded.';
          setPaymentProcessing(false);
          setLimitationError(msg);
          return;
        }
      } catch (limErr) {
        console.warn('[Checkout] Could not check purchase limitations:', limErr);
      }

      // Add to cart
      let cartUrl = `${apiRoutes.Cart}?NumberOfDraws=${numOfDraws}`;
      if (organizationCardId) {
        cartUrl += `&OrganizationCardId=${organizationCardId}`;
      }
      const cartResponse = await api.post(cartUrl, purchases);
      const cartPayload = cartResponse.data?.payload;
      if (!cartPayload || cartPayload.status !== 'Success') {
        const errorMsg = cartPayload?.message || cartPayload?.error
          || cartResponse.data?.errors?.[0]
          || `Cart update failed (status: ${cartPayload?.status || 'unknown'})`;
        throw new Error(errorMsg);
      }

      // Track add-to-cart and checkout start
      trackAddToCart({
        lotteryId: mainDraw?.id || tlfId,
        lotteryType: gameType,
        ticketCount: totalTickets,
        amount: totalPrice,
        currency: 'CAD',
      });
      trackBeginCheckout({
        cartTotal: totalPrice,
        ticketCount: totalTickets,
        lotteryType: gameType,
      });

      // Navigate to checkout immediately — CheckoutPage owns InitializeCheckout
      // so its hang/loading/error states are isolated from the purchase page.
      sessionStorage.removeItem(CART_STORAGE_KEY);
      navigate('/checkout', {
        state: {
          pendingCheckout: true,
          gameType,
          numOfDraws,
          totalPrice,
          totalTickets,
          organizationCardId: organizationCardId || selectedCard,
          envelopeNumber: selectedCardNum,
        },
      });
    } catch (err) {
      console.error('[Checkout] Error:', err);
      setPaymentProcessing(false);
      const errorMsg = err.response?.data?.payload?.message
        || err.response?.data?.payload?.error
        || err.response?.data?.error
        || err.response?.data?.message
        || err.response?.data?.errors?.[0]
        || err.message
        || 'Checkout failed. Please try again.';
      setPaymentError(errorMsg);
    }
  }, [
    packages, packageQuantities, cartItemCount, cartTotal, numWeeks, gameType,
    minimumPurchaseAmount, paymentProcessing, navigate, tlfId, selectedCard, isManualEnvelopeMode,
    isLoggedIn, isGuestCheckoutEnabled, isAnonymousGuestCheckoutEnabled,
    partnerRequiredOnPurchase, isPartnerValidOnPurchase, selectedPartner,
    siteLoading, isDrawClosed, nextDraw, siteDetails, mainDraw, selectedCardNum, tr,
  ]);

  const proceedToCardStep = useCallback(() => {
    setLimitationError(null);
    setPaymentError(null);

    const drawClosed = (!siteLoading && isDrawClosed) || checkDrawClosed({ nextDraw, siteDetails });
    if (drawClosed) {
      navigate('/closed', { replace: true });
      return;
    }

    if (cartItemCount() === 0) {
      setLimitationError(tr('purchase.errors.selectPackage') || 'Please select at least one ticket package.');
      return;
    }

    const totalPrice = cartTotal();
    if (minimumPurchaseAmount > 0 && totalPrice < minimumPurchaseAmount) {
      setLimitationError(`${tr('purchase.errors.minimumPurchasePrefix') || 'Minimum purchase is'} $${minimumPurchaseAmount}.`);
      return;
    }

    if (partnerRequiredOnPurchase && !isPartnerValidOnPurchase) {
      setLimitationError(tr('purchase.errors.partnerRequired') || 'Please choose a partner to support before checking out.');
      try {
        window.dispatchEvent(new CustomEvent('partner-required-focus'));
      } catch {}
      return;
    }

    navigate(appendSelectedPartnerToPath('/chase-the-ace-card'));
  }, [
    siteLoading, isDrawClosed, nextDraw, siteDetails, navigate,
    cartItemCount, cartTotal, minimumPurchaseAmount, tr,
    partnerRequiredOnPurchase, isPartnerValidOnPurchase,
  ]);

  const value = {
    // Game context
    gameType,
    siteDetails,
    mainDraw,
    apiPlans,
    packages,
    siteLoading,

    // Partner selection
    selectedPartner,
    setSelectedPartner,
    purchasePagePartners,
    purchasePageFieldLabel: partnerFieldLabel,
    partnerFieldLabel,
    purchasePageDisplayConfig,
    purchasePagePartnersLoading,
    partnerRequiredOnPurchase,
    isPartnerValidOnPurchase,
    proceedToCardStep,

    // Purchase flow
    step,
    setStep,
    packageQuantities,
    setPackageQuantities,
    numWeeks,
    setNumWeeks,

    // CTA card selection
    selectedCard,
    setSelectedCard,
    selectedCardNum,
    setSelectedCardNum,
    availableCards,
    isManualEnvelopeMode,
    isEncryptedEnvelopeMode,
    isEncryptedEnvelopeAutoFinalize,

    // Validation
    minimumPurchaseAmount,
    limitationError,
    setLimitationError,

    // Terms
    locationConfirmed,
    setLocationConfirmed,
    termsAccepted,
    setTermsAccepted,

    // Tracking
    ticketTrackingFields,
    setTicketTrackingFields,
    trackingLoaded,
    trackingValidationFailed,
    setTrackingValidationFailed,

    // Payment
    paymentProcessing,
    setPaymentProcessing,
    paymentSuccess,
    setPaymentSuccess,
    paymentError,
    setPaymentError,
    isSelecting,
    setIsSelecting,
    selectingRef,

    // Confirmation
    orderRef,
    setOrderRef,
    purchaseDate,
    setPurchaseDate,
    generatingTickets,
    setGeneratingTickets,
    ticketsGenerated,
    setTicketsGenerated,
    generatedTicketNumbers,
    setGeneratedTicketNumbers,

    // Guest info
    guestInfo,
    setGuestInfo,
    guestErrors,
    setGuestErrors,

    // Cart actions
    cartTotal,
    cartItemCount,
    incrementPackage,
    decrementPackage,
    clearCart,
    proceedToCheckout,

    // Navigation
    navigate,
    isLoggedIn,
    isGuestCheckoutEnabled,
    isAnonymousGuestCheckoutEnabled,
  };

  return (
    <PurchasePageContext.Provider value={value}>
      {children}
    </PurchasePageContext.Provider>
  );
}

export function usePurchasePage() {
  const context = useContext(PurchasePageContext);
  if (!context) {
    throw new Error('usePurchasePage must be used within PurchasePageProvider');
  }
  return context;
}
